import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { sponsorshipService } from '../../../Services/sponsorshipService';
import ContractForm from '../../../Components/admin/forms/ContractForm';
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../ui/table';
import { Card, CardHeader, CardTitle, CardContent } from '../../../ui/card';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Search, Filter, AlertCircle, CheckCircle, Plus } from 'lucide-react';

/**
 * Admin Contracts Page
 * Lists all contracts with filtering and status management
 */
const AdminContracts = () => {
  const navigate = useNavigate();
  const contractsApi = sponsorshipService.contracts;
  const sponsorsApi = sponsorshipService.sponsors;
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10 });
  const [filters, setFilters] = useState({ search: '', status: '', sponsor_id: '' });
  const [error, setError] = useState(null);
  const [showContractForm, setShowContractForm] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [feasibilityResult, setFeasibilityResult] = useState(null);
  const [sponsorNames, setSponsorNames] = useState({});

  const loadContracts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await contractsApi.list(filters);
      const contractsList = response.contracts || [];
      setContracts(contractsList);
      setPagination(prev => ({
        ...prev,
        total: response.count || 0,
        totalPages: Math.ceil((response.count || 0) / prev.limit)
      }));

      // Fetch sponsor names for all contracts
      const uniqueSponsorIds = [...new Set(contractsList.map(c => c.sponsor_id).filter(Boolean))];
      const names = {};
      
      await Promise.all(
        uniqueSponsorIds.map(async (sponsorId) => {
          try {
            const sponsor = await sponsorsApi.get(sponsorId);
            names[sponsorId] = sponsor.name || 'Unknown Sponsor';
          } catch (err) {
            console.error(`Failed to load sponsor ${sponsorId}:`, err);
            names[sponsorId] = 'Unknown Sponsor';
          }
        })
      );
      
      setSponsorNames(names);
    } catch (err) {
      setError(err.message || 'Failed to load contracts');
      console.error('Failed to load contracts:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load contracts data
  useEffect(() => {
    loadContracts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, filters]);

  const handleCreateContract = () => {
    setSelectedContract(null);
    setShowContractForm(true);
  };

  const handleEditContract = async (contractId) => {
    try {
      const contract = await contractsApi.get(contractId);
      setSelectedContract(contract);
      setShowContractForm(true);
    } catch (err) {
      setError(err.message || 'Failed to load contract details');
    }
  };

  const handleCloseForm = () => {
    setShowContractForm(false);
    setSelectedContract(null);
  };

  const handleFormSuccess = () => {
    loadContracts();
  };

  const handleFeasibilityCheck = async (contractId) => {
    setLoading(true);
    setError(null);
    setFeasibilityResult(null);
    try {
      const result = await contractsApi.checkFeasibility(contractId);
      setFeasibilityResult(result);
      
      if (result.is_feasible) {
        const confirmActivate = window.confirm(
          `✅ Feasibility Check Passed!\n\n` +
          `Eligible Users: ${result.eligible_users_count?.toLocaleString() || 0}\n` +
          `Available Weeks: ${result.available_weeks?.toLocaleString() || 0}\n\n` +
          `Would you like to activate this contract now?`
        );
        
        if (confirmActivate) {
          await contractsApi.activate(contractId);
          setFeasibilityResult(null);
          loadContracts();
        }
      } else {
        alert(
          `❌ Feasibility Check Failed\n\n` +
          `Reason: ${result.reason || 'Insufficient inventory'}\n` +
          `Required Weeks: ${result.weeks_needed || 0}\n` +
          `Available Weeks: ${result.available_weeks || 0}`
        );
      }
    } catch (err) {
      setError(err.message || 'Failed to run feasibility check');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (contractId, action) => {
    const actionLabels = {
      activate: 'activate',
      pause: 'pause',
      resume: 'resume',
      complete: 'complete'
    };
    
    if (!window.confirm(`Are you sure you want to ${actionLabels[action]} this contract?`)) {
      return;
    }
    try {
      switch (action) {
        case 'activate':
          await contractsApi.activate(contractId);
          break;
        case 'pause':
          await contractsApi.pause(contractId);
          break;
        case 'resume':
          await contractsApi.resume(contractId);
          break;
        case 'complete':
          await contractsApi.complete(contractId);
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }
      await loadContracts();
    } catch (err) {
      setError(err.message || `Failed to ${action} contract`);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { variant: 'secondary', label: 'Draft' },
      active: { variant: 'success', label: 'Active' },
      paused: { variant: 'warning', label: 'Paused' },
      completed: { variant: 'default', label: 'Completed' },
      cancelled: { variant: 'destructive', label: 'Cancelled' },
    };

    const config = statusConfig[status] || { variant: 'secondary', label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getAvailableActions = (status) => {
    const actions = [];

    switch (status) {
      case 'draft':
        actions.push({ key: 'feasibility', label: 'Run Feasibility', action: 'feasibility' });
        break;
      case 'active':
        actions.push({ key: 'pause', label: 'Pause', action: 'pause' });
        break;
      case 'paused':
        actions.push({ key: 'resume', label: 'Resume', action: 'resume' });
        break;
      default:
        break;
    }

    return actions;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Contracts Management</h1>
          <p className="text-muted-foreground mt-1">Manage sponsorship contracts and their lifecycle</p>
        </div>
        <Button onClick={handleCreateContract}>
          <Plus className="h-4 w-4 mr-2" />
          Create Contract
        </Button>
      </div>

      {/* Filters Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="search">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Search contracts..."
                  value={filters.search || ''}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <select
                id="status"
                className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={filters.status || ''}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">All Status</option>
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="sponsor">Sponsor</Label>
              <select
                id="sponsor"
                className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={filters.sponsor_id || ''}
                onChange={(e) => setFilters({ ...filters, sponsor_id: e.target.value })}
              >
                <option value="">All Sponsors</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contracts Table Card */}
      <Card>
        <CardHeader>
          <CardTitle>Contracts ({pagination.total})</CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-muted-foreground">Loading contracts...</div>
            </div>
          ) : contracts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-muted-foreground mb-4">No contracts found</p>
              <Button onClick={handleCreateContract}>
                Create First Contract
              </Button>
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Contract ID</TableHead>
                      <TableHead>Sponsor</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Coverage</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contracts.map((contract) => (
                      <TableRow key={contract._id}>
                        <TableCell>
                          <button
                            className="text-primary hover:underline font-medium"
                            onClick={() => handleEditContract(contract._id)}
                            title="Click to edit contract"
                          >
                            {contract._id?.substring(0, 8)}...
                          </button>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">
                              {sponsorNames[contract.sponsor_id] || 'Loading...'}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              ID: {contract.sponsor_id?.substring(0, 8)}...
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(contract.status)}</TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium capitalize">{contract.coverage_scope}</div>
                            {contract.territories && contract.territories.length > 0 && (
                              <div className="text-sm text-muted-foreground">
                                {contract.territories.length} territories
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">
                              {contract.weeks_purchased} weeks
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {contract.weeks_delivered || 0} delivered
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditContract(contract._id)}
                            >
                              View
                            </Button>
                            {getAvailableActions(contract.status).map((action) => (
                              <Button
                                key={action.key}
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  if (action.action === 'feasibility') {
                                    handleFeasibilityCheck(contract._id);
                                  } else {
                                    handleStatusChange(contract._id, action.action);
                                  }
                                }}
                              >
                                {action.label}
                              </Button>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between pt-4">
                  <div className="text-sm text-muted-foreground">
                    Showing {contracts.length} of {pagination.total} contracts
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page <= 1}
                      onClick={() => handlePageChange(pagination.page - 1)}
                    >
                      Previous
                    </Button>
                    <span className="text-sm">
                      Page {pagination.page} of {pagination.totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page >= pagination.totalPages}
                      onClick={() => handlePageChange(pagination.page + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Contract Form Modal */}
      {showContractForm && (
        <ContractForm
          contract={selectedContract}
          onClose={handleCloseForm}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
};

export default AdminContracts;
